import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { clsx } from 'clsx';
import { WrapperWidget } from '@agentselly/ui';
import { useForm, useFormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useFormRegistration } from '~/context/PropertyEditContext';
import { useAutosaveForm } from '~/hooks/useAutosaveForm';
import { Checkbox } from '~/components/ui/Checkbox';
import type {
  PropertyAddressType,
  PropertyFormType,
} from '@agentselly/immodossier-shared';
import {
  ActionSchema,
  AppAction,
  EnergyLabelEnums,
  HeatingGenerationEnums,
  PropertySubTypesEnums,
  PropertyTypeEnums,
} from '@agentselly/immodossier-shared';
import { getEnumValues } from '~/shared/getEnumValues';
import { useV3Redesign } from '~/helper/useV3Redesign';
import { PropertyFormWidgetV3 } from './PropertyFormWidgetV3';

interface CollapsibleSectionProps {
  readonly title: string;
  readonly children: React.ReactNode;
  readonly defaultOpen?: boolean;
}

function CollapsibleSection({
  title,
  children,
  defaultOpen = true,
}: CollapsibleSectionProps) {
  const [
    isOpen,
    setIsOpen,
  ] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-800/50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <h3 className="font-semibold text-base px-1">{title}</h3>
        <svg
          className={clsx(
            'w-5 h-5 text-gray-400 dark:text-gray-500 dark:text-gray-400 transition-transform',
            {
              'rotate-180': isOpen,
            },
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && <div className="p-3 pt-0">{children}</div>}
    </div>
  );
}

interface SaveStatusIndicatorProps {
  readonly status: 'idle' | 'saving' | 'saved' | 'error' | 'auth_error';
  readonly t: (key: string) => string;
  readonly onRetry?: () => void;
}

function SaveStatusIndicator({ status, t, onRetry }: SaveStatusIndicatorProps) {
  if (status === 'idle') {
    return (
      <div
        className="flex items-center gap-1.5 text-sm text-base-content/40"
        data-testid="autosave-ready"
      >
        <span className="w-2 h-2 rounded-full bg-base-content/20"></span>
        <span>{t('autosave_ready')}</span>
      </div>
    );
  }

  if (status === 'saving') {
    return (
      <div
        className="flex items-center gap-1.5 text-sm text-base-content/60"
        data-testid="autosave-saving"
      >
        <span className="loading loading-spinner loading-xs"></span>
        <span>{t('autosave_saving')}</span>
      </div>
    );
  }

  if (status === 'saved') {
    return (
      <div
        className="flex items-center gap-1.5 text-sm text-success"
        data-testid="autosave-saved"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
        <span>{t('autosave_saved')}</span>
      </div>
    );
  }

  if (status === 'error' || status === 'auth_error') {
    return (
      <div
        className="flex items-center gap-2 text-sm text-error"
        data-testid="autosave-error"
      >
        <svg
          className="w-4 h-4 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>{t('autosave_error')}</span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="ml-1 px-2 py-0.5 rounded bg-error text-white text-xs hover:bg-error/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/50"
            data-testid="autosave-retry"
          >
            {t('autosave_retry')}
          </button>
        )}
      </div>
    );
  }

  return null;
}

interface Props {
  readonly dossierId: string;
  readonly address: PropertyAddressType;
  readonly property: PropertyFormType;
}

function LegacyPropertyFormWidget(props: Props) {
  const { t } = useTranslate();

  const combinedDefaults = { ...props.property, ...props.address };
  const defaultValues = Object.fromEntries(
    Object.entries(combinedDefaults).map(
      ([
        key,
        value,
      ]) => [
        key,
        value ?? '',
      ],
    ),
  );

  const form = useForm<PropertyFormType & PropertyAddressType>({
    defaultValues: defaultValues,
    resolver: zodResolver(ActionSchema[AppAction.PropertyForm] as any),
  });

  useFormState({
    control: form.control,
  });

  const { saveStatus, triggerManualSave, retrySave } = useAutosaveForm({
    form,
    dossierId: props.dossierId,
    propertyId: (props.property as any)?.id || (props.property as any)?._id,
    action: AppAction.PropertyForm,
    debounceMs: 3000,
    showToastOnFirstSave: true,
    onSaveError: () => {
      toast.error(t('property_form_not_saved'));
    },
  });

  useFormRegistration(triggerManualSave);

  const propertyTypes = getEnumValues(PropertyTypeEnums as any);
  const propertySubtypes = getEnumValues(PropertySubTypesEnums as any);
  const energyLabels = getEnumValues(EnergyLabelEnums as any);
  const heatingGenerationTypes = getEnumValues(HeatingGenerationEnums as any);

  const isSaving = saveStatus === 'saving';

  return (
    <WrapperWidget>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold">
            {t('evaluation_property_information_form_title')}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <SaveStatusIndicator
            status={saveStatus}
            t={t}
            onRetry={retrySave}
          />
        </div>
      </div>
      <p className="text-sm text-base-content/70 mb-4">
        {t('evaluation_property_information_form_text')}
      </p>

      <form
        className={clsx('flex flex-col gap-3 w-full', {
          'opacity-80 animate-pulse pointer-events-none': isSaving,
        })}
      >
        <CollapsibleSection title={t('property_form_address_title')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_property_street_name')}
                </span>
              </label>
              <input
                {...form.register('street')}
                className="input input-bordered w-full"
              />
              <Error
                name="street"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_property_street_number_name')}
                </span>
              </label>
              <input
                {...form.register('streetNumber')}
                className="input input-bordered w-full"
              />
              <Error
                name="streetNumber"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_property_zip_name')}
                </span>
              </label>
              <input
                {...form.register('zip')}
                className="input input-bordered w-full"
              />
              <Error
                name="zip"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_property_location_name')}
                </span>
              </label>
              <input
                {...form.register('location')}
                className="input input-bordered w-full"
              />
              <Error
                name="location"
                formState={form.formState}
              />
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title={t('property_form_property_information_title')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-3">
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_property_type_name')}
                </span>
              </label>
              <select
                {...form.register('propertyType')}
                data-testid="select-propertyType"
                className="select select-bordered w-full"
              >
                <option value="">{t('property_form_please_choose')}</option>
                {propertyTypes.map((type, idx) => (
                  <option
                    key={idx}
                    value={type}
                  >
                    {t(`property_form_property_types_label_${type}`)}
                  </option>
                ))}
              </select>
              <Error
                name="propertyType"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_property_subtype_name')}
                </span>
              </label>
              <select
                {...form.register('propertySubtype')}
                data-testid="select-propertySubtype"
                className="select select-bordered w-full"
              >
                <option value="">{t('property_form_please_choose')}</option>
                {propertySubtypes.map((subtype, idx) => (
                  <option
                    key={idx}
                    value={subtype}
                  >
                    {t(`property_form_property_subtypes_label_${subtype}`)}
                  </option>
                ))}
              </select>
              <Error
                name="propertySubtype"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_building_year_name')}
                </span>
              </label>
              <input
                type="number"
                {...form.register('buildingYear', {
                  setValueAs: (v) => (v === '' ? undefined : Number(v)),
                })}
                className="input input-bordered w-full"
              />
              <Error
                name="buildingYear"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_living_area_name')}
                </span>
              </label>
              <input
                type="number"
                {...form.register('livingArea', {
                  setValueAs: (v) => (v === '' ? undefined : Number(v)),
                })}
                className="input input-bordered w-full"
              />
              <Error
                name="livingArea"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_land_area_name')}
                </span>
              </label>
              <input
                type="number"
                {...form.register('landArea', {
                  setValueAs: (v) => (v === '' ? undefined : Number(v)),
                })}
                className="input input-bordered w-full"
              />
              <Error
                name="landArea"
                formState={form.formState}
              />
            </div>
            {form.watch('propertyType') === 'apartment' && (
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    {t(
                      'property_form_balcony_area_name',
                      'Balkon / Terrasse (m²)',
                    )}
                  </span>
                </label>
                <input
                  type="number"
                  {...form.register('balconyArea', {
                    setValueAs: (v) => (v === '' ? undefined : Number(v)),
                  })}
                  className="input input-bordered w-full"
                />
                <Error
                  name="balconyArea"
                  formState={form.formState}
                />
              </div>
            )}
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_renovation_year_name')}
                </span>
              </label>
              <input
                type="number"
                {...form.register('renovationYear', {
                  setValueAs: (v) => (v === '' ? undefined : Number(v)),
                })}
                className="input input-bordered w-full"
              />
              <Error
                name="renovationYear"
                formState={form.formState}
              />
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title={t('property_form_numbers_title')}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3">
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_number_of_rooms_name')}
                </span>
              </label>
              <input
                type="number"
                step="0.5"
                {...form.register('numberOfRooms', {
                  setValueAs: (v) => (v === '' ? undefined : Number(v)),
                })}
                className="input input-bordered w-full"
              />
              <Error
                name="numberOfRooms"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_number_of_bathrooms_name')}
                </span>
              </label>
              <input
                type="number"
                {...form.register('numberOfBathrooms', {
                  setValueAs: (v) => (v === '' ? undefined : Number(v)),
                })}
                className="input input-bordered w-full"
              />
              <Error
                name="numberOfBathrooms"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_number_of_indoor_parking_spaces_name')}
                </span>
              </label>
              <input
                type="number"
                {...form.register('numberOfIndoorParkingSpaces', {
                  setValueAs: (v) => (v === '' ? undefined : Number(v)),
                })}
                className="input input-bordered w-full"
              />
              <Error
                name="numberOfIndoorParkingSpaces"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_number_of_outdoor_parking_spaces_name')}
                </span>
              </label>
              <input
                type="number"
                {...form.register('numberOfOutdoorParkingSpaces', {
                  setValueAs: (v) => (v === '' ? undefined : Number(v)),
                })}
                className="input input-bordered w-full"
              />
              <Error
                name="numberOfOutdoorParkingSpaces"
                formState={form.formState}
              />
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title={t('property_form_features_title') || 'Features'}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3">
            <div className="space-y-3">
              <Checkbox
                id="hasLift"
                label={t('property_form_has_lift_name')}
                checked={!!form.watch('hasLift')}
                onChange={(e) =>
                  form.setValue('hasLift', e.target.checked, {
                    shouldDirty: true,
                  })
                }
                disabled={isSaving}
              />
              <Checkbox
                id="hasSolarPanels"
                label={t('property_form_has_solar_panels_name')}
                checked={!!form.watch('hasSolarPanels')}
                onChange={(e) =>
                  form.setValue('hasSolarPanels', e.target.checked, {
                    shouldDirty: true,
                  })
                }
                disabled={isSaving}
              />
              <Checkbox
                id="hasPool"
                label={t('property_form_has_pool_name')}
                checked={!!form.watch('hasPool')}
                onChange={(e) =>
                  form.setValue('hasPool', e.target.checked, {
                    shouldDirty: true,
                  })
                }
                disabled={isSaving}
              />
            </div>
            <div className="space-y-3">
              <Checkbox
                id="isNew"
                label={t('property_form_is_new_name')}
                checked={!!form.watch('isNew')}
                onChange={(e) =>
                  form.setValue('isNew', e.target.checked, {
                    shouldDirty: true,
                  })
                }
                disabled={isSaving}
              />
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    {t('property_form_number_of_floors_in_building_name')}
                  </span>
                </label>
                <input
                  type="number"
                  {...form.register('numberOfFloorsInBuilding', {
                    setValueAs: (v) => (v === '' ? undefined : Number(v)),
                  })}
                  className="input input-bordered w-full"
                />
                <Error
                  name="numberOfFloorsInBuilding"
                  formState={form.formState}
                />
              </div>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection title={t('property_form_energy_title') || 'Energy'}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_energy_label_name')}
                </span>
              </label>
              <select
                {...form.register('energyLabel')}
                data-testid="select-energyLabel"
                className="select select-bordered w-full"
              >
                <option value="">{t('property_form_please_choose')}</option>
                {energyLabels.map((label, idx) => (
                  <option
                    key={idx}
                    value={label}
                  >
                    {t(`property_form_energy_label_${label}`)}
                  </option>
                ))}
              </select>
              <Error
                name="energyLabel"
                formState={form.formState}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  {t('property_form_heating_generation_type_name')}
                </span>
              </label>
              <select
                {...form.register('heatingGenerationType')}
                data-testid="select-heatingGenerationType"
                className="select select-bordered w-full"
              >
                <option value="">{t('property_form_please_choose')}</option>
                {heatingGenerationTypes.map((type, idx) => (
                  <option
                    key={idx}
                    value={type}
                  >
                    {t(`property_form_energy_label_${type}`)}
                  </option>
                ))}
              </select>
              <Error
                name="heatingGenerationType"
                formState={form.formState}
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* Manueller Speichern-Button als Fallback für alte Geräte.
            Im Gegensatz zum Autosave (der auf watch-Callbacks angewiesen ist,
            die auf langsamen Geräten nie feuern können) liest saveNow() die
            aktuellen Formularwerte via form.getValues() und sendet sie sofort.
            Der Button ist immer klickbar — isDirty wird NICHT als disabled-Kriterium
            verwendet, da isDirty auf dem watch-Callback-Tracking basiert, das auf
            alten Geräten unzuverlässig sein kann. */}
        <div className="pt-2">
          <button
            type="button"
            data-testid="manual-save-button"
            onClick={() => triggerManualSave()}
            disabled={saveStatus === 'saving'}
            className={clsx(
              'btn btn-sm',
              saveStatus === 'saving'
                ? 'btn-disabled'
                : 'btn-outline btn-primary',
            )}
          >
            {saveStatus === 'saving' ? (
              <>
                <span className="loading loading-spinner loading-xs"></span>
                {t('autosave_saving', 'Speichern...')}
              </>
            ) : (
              t('autosave_save', 'Speichern')
            )}
          </button>
        </div>
      </form>
    </WrapperWidget>
  );
}

export function PropertyFormWidget(props: Props) {
  return useV3Redesign() ? (
    <PropertyFormWidgetV3 {...props} />
  ) : (
    <LegacyPropertyFormWidget {...props} />
  );
}

interface ErrorProps {
  readonly name: string;
  readonly formState: any;
}

function Error(props: ErrorProps) {
  const errorMessage = props?.formState?.errors?.[props.name]?.message || '';
  if (!errorMessage) return null;
  return (
    <p className="label-text-alt text-error pt-1">{String(errorMessage)}</p>
  );
}
